import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader, ArrowLeft } from 'lucide-react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import './OTPVerification.css';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const OTPVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useSecureAuth();

  const emailParam = searchParams.get('email') || '';
  const email = emailParam ? decodeURIComponent(emailParam) : '';
  const purpose = searchParams.get('purpose') === 'login' ? 'login' : 'signup';
  const isLogin = purpose === 'login';

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [trustDevice, setTrustDevice] = useState(true); // login only — default ON

  const inputRefs = useRef([]);

  // If email is missing, route back to whichever entry page makes sense
  useEffect(() => {
    if (!email) {
      navigate(isLogin ? '/login' : '/signup', { replace: true });
    }
  }, [email, isLogin, navigate]);

  // Focus the first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Mask the email for display: m****@example.com
  const maskedEmail = email
    ? (() => {
        const [local, domain] = email.split('@');
        if (!domain) return email;
        if (local.length <= 2) return `${local[0]}*@${domain}`;
        return `${local[0]}${'*'.repeat(Math.min(4, local.length - 1))}@${domain}`;
      })()
    : '';

  const codeString = digits.join('');
  const isCodeComplete = codeString.length === CODE_LENGTH;

  const setDigit = (idx, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const focusInput = (idx) => {
    if (idx >= 0 && idx < CODE_LENGTH && inputRefs.current[idx]) {
      inputRefs.current[idx].focus();
      inputRefs.current[idx].select?.();
    }
  };

  const handleInput = (idx, raw) => {
    setError('');
    setInfo('');
    // Only digits, only the last typed character
    const digit = (raw.match(/\d/g) || []).pop() || '';
    setDigit(idx, digit);
    if (digit && idx < CODE_LENGTH - 1) {
      focusInput(idx + 1);
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        // Clear current; stay focused
        setDigit(idx, '');
      } else if (idx > 0) {
        // Move back and clear previous
        focusInput(idx - 1);
        setDigit(idx - 1, '');
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      focusInput(idx - 1);
    } else if (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
      e.preventDefault();
      focusInput(idx + 1);
    } else if (e.key === 'Enter' && isCodeComplete) {
      e.preventDefault();
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    setTimeout(() => focusInput(focusIdx), 0);
  };

  const handleVerify = useCallback(async () => {
    if (loading) return;
    if (!isCodeComplete) {
      setError('Enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await fetch('/api/merchant/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          code: codeString,
          purpose,
          trustDevice: isLogin ? trustDevice : false
        }),
      });
      const data = await res.json();

      if (data.success) {
        // Cookie was set by backend. Refresh auth context, then route.
        await refreshUser();
        const fallback = isLogin ? '/dashboard' : '/onboarding';
        navigate(data.redirectTo || fallback, { replace: true });
        return;
      }

      // Error handling by code
      switch (data.code) {
        case 'INVALID_OTP':
          setError(
            typeof data.attemptsRemaining === 'number'
              ? `Invalid code. ${data.attemptsRemaining} attempt${data.attemptsRemaining === 1 ? '' : 's'} left.`
              : 'Invalid code.'
          );
          setAttemptsRemaining(data.attemptsRemaining ?? null);
          // Clear the inputs so user can retype
          setDigits(Array(CODE_LENGTH).fill(''));
          focusInput(0);
          break;
        case 'OTP_EXPIRED':
          setError('Code expired. Tap "Resend code" to get a new one.');
          break;
        case 'ACCOUNT_LOCKED': {
          const mins = Math.max(1, Math.ceil((data.retryAfter || 1800) / 60));
          setError(`Too many attempts. Try again in ${mins} minute${mins === 1 ? '' : 's'}.`);
          break;
        }
        default:
          setError(data.error || 'Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('[OTPVerification] verify error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, isCodeComplete, email, codeString, purpose, isLogin, trustDevice, refreshUser, navigate]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (isCodeComplete && !loading && !error) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCodeComplete]);

  const handleResend = async () => {
    if (resending || cooldown > 0) return;

    setResending(true);
    setError('');
    setInfo('');

    try {
      const res = await fetch('/api/merchant/auth/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, purpose }),
      });
      const data = await res.json();

      if (res.status === 429 && data.code === 'THROTTLED') {
        setCooldown(data.retryAfter || RESEND_COOLDOWN_SECONDS);
        setError(data.error || 'Please wait before requesting another code.');
        return;
      }

      if (data.success) {
        setInfo('A new code has been sent to your email.');
        setCooldown(RESEND_COOLDOWN_SECONDS);
        setDigits(Array(CODE_LENGTH).fill(''));
        setAttemptsRemaining(null);
        focusInput(0);
      } else {
        setError(data.error || 'Could not resend code. Please try again.');
      }
    } catch (err) {
      console.error('[OTPVerification] resend error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  const backTo = isLogin ? '/login' : '/signup';
  const backLabel = isLogin ? 'Back to sign in' : 'Back to signup';
  const title = isLogin ? "Confirm it's you" : 'Verify your email';
  const subtitleLead = isLogin
    ? 'For your security, we sent a 6-digit code to'
    : 'We sent a 6-digit code to';
  const submitLabel = isLogin ? 'Sign in' : 'Verify code';
  const submittingLabel = isLogin ? 'Signing in...' : 'Verifying...';
  const wrongEmailLabel = isLogin
    ? { prompt: 'Wrong email?', linkText: 'Use a different one' }
    : { prompt: 'Wrong email?', linkText: 'Use a different one' };

  return (
    <div className="otpv-container">
      <div className="otpv-card">
        <Link to={backTo} className="otpv-back" aria-label={backLabel}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>

        <div className="otpv-header">
          <img
            src="/logo192.png"
            alt="Revstar"
            className="otpv-logo"
          />
          <h1 className="otpv-title">{title}</h1>
          <p className="otpv-subtitle">
            {subtitleLead}{' '}
            <span className="otpv-email">{maskedEmail}</span>
          </p>
        </div>

        {error && (
          <div className="otpv-error" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        {info && !error && (
          <div className="otpv-info" role="status" aria-live="polite">
            {info}
          </div>
        )}

        <div
          className="otpv-code"
          onPaste={handlePaste}
          role="group"
          aria-label="6-digit verification code"
        >
          {digits.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => {
                inputRefs.current[idx] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={idx === 0 ? 'one-time-code' : 'off'}
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInput(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onFocus={(e) => e.target.select()}
              disabled={loading}
              className={`otpv-input ${digit ? 'otpv-input-filled' : ''} ${error ? 'otpv-input-error' : ''}`}
              aria-label={`Digit ${idx + 1}`}
            />
          ))}
        </div>

        {isLogin && (
          <label className="otpv-trust">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              disabled={loading}
            />
            <span className="otpv-trust-box" aria-hidden="true">
              {trustDevice && (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="otpv-trust-text">Trust this device for 30 days</span>
          </label>
        )}

        <button
          type="button"
          className="otpv-cta"
          onClick={handleVerify}
          disabled={!isCodeComplete || loading}
        >
          {loading ? (
            <>
              <Loader size={18} className="otpv-spinner" />
              <span>{submittingLabel}</span>
            </>
          ) : (
            <span>{submitLabel}</span>
          )}
        </button>

        <div className="otpv-resend">
          <span>Didn't get the code? </span>
          {cooldown > 0 ? (
            <span className="otpv-resend-cooldown">Resend in {cooldown}s</span>
          ) : (
            <button
              type="button"
              className="otpv-resend-btn"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          )}
        </div>

        <div className="otpv-footer">
          {wrongEmailLabel.prompt}{' '}
          <Link to={backTo} className="otpv-footer-link">
            {wrongEmailLabel.linkText}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
